import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";

/**
 * useBatchedBackfill — extracts the identical batched-loop pattern used
 * by the "fill uses", "fill life cycle", and "fill dormancy" actions.
 * Each one loops a POST endpoint in small batches with linear backoff
 * on transient failure so a mid-run network blip doesn't abandon
 * partial progress.
 *
 * @param {object} cfg
 * @param {string} cfg.statusUrl    GET endpoint returning { total, pending, done }
 * @param {string} cfg.runUrl       POST endpoint to trigger a batch (query-string batch size baked in)
 * @param {object} cfg.labels       { starting, progress, retrying, partial, success } — i18n key names
 * @param {number} cfg.maxIterations  Safety cap on the outer loop (default 500)
 * @param {number} cfg.maxConsecutiveFailures  Bail-out threshold (default 4)
 * @param {number} cfg.timeoutMs    Per-request timeout (default 90s < Cloudflare's 100s edge cutoff)
 */
export function useBatchedBackfill({
  statusUrl,
  runUrl,
  labels,
  maxIterations = 500,
  maxConsecutiveFailures = 4,
  timeoutMs = 90 * 1000,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get(statusUrl);
      setStatus(data);
    } catch {
      // Non-critical — the caller decides what to render without status.
      setStatus(null);
    }
  }, [statusUrl]);

  const run = useCallback(async () => {
    setRunning(true);
    let totalUpdated = 0;
    let consecutiveFailures = 0;
    const toastId = toast.loading(t(labels.starting));
    try {
      for (let i = 0; i < maxIterations; i++) {
        try {
          const { data } = await apiClient.post(runUrl, {}, { timeout: timeoutMs });
          consecutiveFailures = 0;
          totalUpdated += data.updated || 0;
          toast.loading(
            t(labels.progress, { n: totalUpdated, remaining: data.remaining }),
            { id: toastId }
          );
          if (!data.remaining) break;
          // No progress + no remaining drop → server has nothing new; bail.
          if (!data.updated && (data.errors || []).length === 0) break;
        } catch {
          consecutiveFailures++;
          if (consecutiveFailures >= maxConsecutiveFailures) {
            toast.error(t(labels.partial, { n: totalUpdated }), { id: toastId });
            return;
          }
          toast.loading(
            t(labels.retrying, { n: totalUpdated, attempt: consecutiveFailures }),
            { id: toastId }
          );
          // Linear backoff: 3s, 6s, 9s, 12s
          await new Promise((r) => setTimeout(r, 3000 * consecutiveFailures));
        }
      }
      toast.success(t(labels.success, { n: totalUpdated }), { id: toastId });
      await loadStatus();
    } finally {
      setRunning(false);
    }
  }, [t, labels, maxIterations, maxConsecutiveFailures, runUrl, timeoutMs, loadStatus]);

  return { status, running, run, loadStatus };
}
